import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import {
  getAllBarStyles,
  getBarStyleById,
  DEFAULT_BAR_STYLE_ID,
  BAR_STYLES_REGISTRY,
} from '../barStyles/BarStyleRegistry';

// Context object
const BarStyleContext = createContext();

// Quick helper to validate ids
const isValidBarStyleId = (id) => !!BAR_STYLES_REGISTRY[id];

export const BarStyleProvider = ({ children, initialBarStyleId }) => {
  // List of available bar styles
  const [availableBarStyles, setAvailableBarStyles] = useState([]);

  // Selected id (persisted in localStorage)
  const [selectedBarStyleId, setSelectedBarStyleId] = useState(() => {
    // Allow overriding via prop (e.g. PDF export)
    if (initialBarStyleId && isValidBarStyleId(initialBarStyleId)) {
      return initialBarStyleId;
    }
    try {
      const savedId = localStorage.getItem('selectedBarStyleId');
      return isValidBarStyleId(savedId) ? savedId : DEFAULT_BAR_STYLE_ID;
    } catch {
      return DEFAULT_BAR_STYLE_ID;
    }
  });

  // Load registry once (component mount)
  useEffect(() => {
    try {
      setAvailableBarStyles(getAllBarStyles());
    } catch (err) {
      console.error('BarStyleContext: failed to load bar styles', err);
    }
  }, []);

  // Persist changes
  useEffect(() => {
    if (!initialBarStyleId) {
      try {
        localStorage.setItem('selectedBarStyleId', selectedBarStyleId);
      } catch (err) {
        console.error('BarStyleContext: failed to save id', err);
      }
    }
  }, [selectedBarStyleId, initialBarStyleId]);

  // Safe setter
  const wrappedSetSelectedBarStyleId = useCallback((id) => {
    if (isValidBarStyleId(id)) {
      setSelectedBarStyleId(id);
    } else {
      console.error('BarStyleContext: invalid style id', id);
    }
  }, []);

  // Active style object
  const activeBarStyle = useMemo(() => getBarStyleById(selectedBarStyleId), [selectedBarStyleId]);

  const value = useMemo(() => ({
    availableBarStyles,
    selectedBarStyleId,
    setSelectedBarStyleId: wrappedSetSelectedBarStyleId,
    activeBarStyle,
  }), [availableBarStyles, selectedBarStyleId, wrappedSetSelectedBarStyleId, activeBarStyle]);

  return <BarStyleContext.Provider value={value}>{children}</BarStyleContext.Provider>;
};

export const useBarStyle = () => {
  const context = useContext(BarStyleContext);
  if (!context) throw new Error('useBarStyle must be used within a BarStyleProvider');
  return context;
}; 